import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import Cancel from "@mui/icons-material/Cancel";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import VerifiedUser from "@mui/icons-material/VerifiedUser";
import WarningAmber from "@mui/icons-material/WarningAmber";
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Fade,
  Typography,
} from "@mui/material";

import { dayjs } from "@thalamos/common";

export type S12VerificationOverlayProps = {
  open: boolean;
  isChecking: boolean;
  status?: "found" | "not-found" | "error";
  section12ExpiryDate?: string;
  isConflict?: boolean;
  localExpiryDate?: string;
  onClose: () => void;
  onUseProfileDate?: () => void;
  onUseApiDate?: () => void;
};

const AUTO_DISMISS_SUCCESS_MS = 3500;
const AUTO_DISMISS_FAILURE_MS = 3000;

export function S12VerificationOverlay({
  open,
  isChecking,
  status,
  section12ExpiryDate,
  isConflict,
  localExpiryDate,
  onClose,
  onUseProfileDate,
  onUseApiDate,
}: S12VerificationOverlayProps) {
  const [showResult, setShowResult] = useState(false);
  const backdropRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      backdropRef.current?.focus();
    }
  }, [open]);

  useEffect(() => {
    if (isChecking || !status) {
      setShowResult(false);
      return;
    }

    const enterTimer = setTimeout(() => setShowResult(true), 200);

    if (isConflict) {
      return () => clearTimeout(enterTimer);
    }

    const dismissMs =
      status === "found" ? AUTO_DISMISS_SUCCESS_MS : AUTO_DISMISS_FAILURE_MS;
    const dismissTimer = setTimeout(onClose, dismissMs);

    return () => {
      clearTimeout(enterTimer);
      clearTimeout(dismissTimer);
    };
  }, [isChecking, status, isConflict, onClose]);

  if (!open) return null;

  const ariaLabel = isChecking
    ? "Checking Section 12 approval"
    : isConflict
      ? "Expiry date mismatch"
      : status === "found"
        ? "Section 12 Approved"
        : status === "not-found"
          ? "No Section 12 approval found"
          : "Unable to check Section 12 approval";

  return (
    <Backdrop
      ref={backdropRef}
      role="dialog"
      aria-modal
      aria-label={ariaLabel}
      tabIndex={-1}
      open={open}
      onKeyDown={(e: KeyboardEvent) => {
        if (e.key === "Escape" && !isConflict) onClose();
      }}
      sx={{
        zIndex: (theme) => theme.zIndex.modal + 1,
        backgroundColor: "rgba(11, 18, 44, 0.75)",
        flexDirection: "column",
        gap: 3,
        outline: "none",
      }}
    >
      {isChecking && (
        <Fade in>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <CircularProgress size={64} sx={{ color: "#8ABE23" }} />
            <Typography variant="h6" sx={{ color: "#fff", fontWeight: 500 }}>
              Checking Section 12 approval…
            </Typography>
          </Box>
        </Fade>
      )}

      {showResult &&
        status === "found" &&
        section12ExpiryDate &&
        !isConflict && (
          <Fade in timeout={600}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
                animation: "s12-pop-in 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)",
                "@keyframes s12-pop-in": {
                  "0%": { transform: "scale(0.3)", opacity: 0 },
                  "100%": { transform: "scale(1)", opacity: 1 },
                },
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    width: 120,
                    height: 120,
                    borderRadius: "50%",
                    background:
                      "radial-gradient(circle, rgba(138, 190, 35, 0.25) 0%, transparent 70%)",
                    animation: "s12-pulse 2s ease-in-out infinite",
                    "@keyframes s12-pulse": {
                      "0%, 100%": { transform: "scale(1)", opacity: 0.6 },
                      "50%": { transform: "scale(1.3)", opacity: 0.2 },
                    },
                  }}
                />
                <VerifiedUser
                  sx={{
                    fontSize: 72,
                    color: "#8ABE23",
                    filter: "drop-shadow(0 0 12px rgba(138, 190, 35, 0.5))",
                  }}
                />
                <CheckCircleOutline
                  sx={{
                    position: "absolute",
                    bottom: -4,
                    right: -4,
                    fontSize: 28,
                    color: "#fff",
                    backgroundColor: "#008758",
                    borderRadius: "50%",
                  }}
                />
              </Box>

              <Typography
                variant="h5"
                sx={{ color: "#fff", fontWeight: 600, mt: 1 }}
              >
                Section 12 Approved
              </Typography>

              <Box
                sx={{
                  backgroundColor: "rgba(138, 190, 35, 0.15)",
                  border: "1px solid rgba(138, 190, 35, 0.4)",
                  borderRadius: 2,
                  px: 3,
                  py: 1.5,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: "rgba(255, 255, 255, 0.7)", mb: 0.5 }}
                >
                  Accreditation valid until
                </Typography>
                <Typography
                  variant="h6"
                  sx={{ color: "#fff", fontWeight: 600 }}
                >
                  {dayjs(section12ExpiryDate).format("D MMMM YYYY")}
                </Typography>
              </Box>

              <Button
                variant="outlined"
                onClick={onClose}
                sx={{
                  color: "#fff",
                  borderColor: "rgba(255, 255, 255, 0.4)",
                  mt: 1,
                  "&:hover": {
                    borderColor: "#fff",
                    backgroundColor: "rgba(255, 255, 255, 0.08)",
                  },
                }}
              >
                Close
              </Button>
            </Box>
          </Fade>
        )}

      {showResult && isConflict && section12ExpiryDate && localExpiryDate && (
        <Fade in timeout={600}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
              maxWidth: 440,
            }}
          >
            <WarningAmber sx={{ fontSize: 64, color: "#F5A623" }} />

            <Typography
              variant="h5"
              sx={{ color: "#fff", fontWeight: 600, textAlign: "center" }}
            >
              Expiry date mismatch
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: "rgba(255, 255, 255, 0.7)",
                textAlign: "center",
                lineHeight: 1.6,
              }}
            >
              The Section 12 register shows a different expiry date to your
              profile. Which date would you like to use?
            </Typography>

            <Box
              sx={{
                display: "flex",
                gap: 2,
                mt: 1,
                width: "100%",
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  backgroundColor: "rgba(255, 255, 255, 0.08)",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  borderRadius: 2,
                  px: 2,
                  py: 1.5,
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: "rgba(255, 255, 255, 0.5)" }}
                >
                  Your profile
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: "#fff", fontWeight: 600, mt: 0.5 }}
                >
                  {dayjs(localExpiryDate).format("D MMMM YYYY")}
                </Typography>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  backgroundColor: "rgba(245, 166, 35, 0.12)",
                  border: "1px solid rgba(245, 166, 35, 0.4)",
                  borderRadius: 2,
                  px: 2,
                  py: 1.5,
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: "rgba(255, 255, 255, 0.5)" }}
                >
                  Section 12 register
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: "#fff", fontWeight: 600, mt: 0.5 }}
                >
                  {dayjs(section12ExpiryDate).format("D MMMM YYYY")}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 2,
                mt: 1,
              }}
            >
              <Button
                variant="outlined"
                onClick={onUseProfileDate}
                sx={{
                  color: "#fff",
                  borderColor: "rgba(255, 255, 255, 0.4)",
                  "&:hover": {
                    borderColor: "#fff",
                    backgroundColor: "rgba(255, 255, 255, 0.08)",
                  },
                }}
              >
                Keep profile date
              </Button>
              <Button
                variant="contained"
                onClick={onUseApiDate}
                sx={{
                  backgroundColor: "#8ABE23",
                  color: "#fff",
                  "&:hover": {
                    backgroundColor: "#7AAD13",
                  },
                }}
              >
                Use register date
              </Button>
            </Box>
          </Box>
        </Fade>
      )}

      {showResult && (status === "error" || status === "not-found") && (
        <Fade in timeout={400}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Cancel sx={{ fontSize: 64, color: "#C74418" }} />
            <Typography
              variant="h6"
              sx={{ color: "#fff", fontWeight: 500, textAlign: "center" }}
            >
              {status === "not-found"
                ? "No Section 12 approval found for this GMC number"
                : "Unable to check Section 12 approval right now"}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "rgba(255, 255, 255, 0.6)", textAlign: "center" }}
            >
              {status === "not-found"
                ? "You can still set your Section 12 details manually below."
                : "Please try again later, or set your details manually."}
            </Typography>

            <Button
              variant="outlined"
              onClick={onClose}
              sx={{
                color: "#fff",
                borderColor: "rgba(255, 255, 255, 0.4)",
                mt: 1,
                "&:hover": {
                  borderColor: "#fff",
                  backgroundColor: "rgba(255, 255, 255, 0.08)",
                },
              }}
            >
              Close
            </Button>
          </Box>
        </Fade>
      )}
    </Backdrop>
  );
}
